// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Auto-fit viewport: frame the map on the fleet's own sites.
//
// The requirement is that the map centres and zooms on the data centers
// rather than showing a fixed national view — an ops manager with three
// sites in Tennessee should see Tennessee, not the whole country with three
// dots huddled in one corner. Zoom is therefore a pure function of the site
// set, with no manual zoom control: there is nothing for the user to reset,
// and the framing is correct by construction as sites are added or removed.

import { GEOMETRY_BOUNDS } from './usGeometry';
import type { Point } from './projection';

export interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Fraction of the fitted span added as breathing room on every side, so
 * markers (and their labels) near the edge aren't flush against the frame.
 */
const PADDING_RATIO = 0.12;

/**
 * Floor on the fitted span, in geometry units.
 *
 * Without it, a single site fits a zero-size box and a tightly clustered
 * fleet fits a tiny one — both zoom to a meaningless blur of one state's
 * interior, with no recognisable border to read position against. The
 * artwork is ~917 units across for ~4,500km of country, so 260 units is
 * roughly a 1,300km multi-state region: close enough to read the fleet,
 * wide enough to still recognise where in the country you are.
 */
const MIN_SPAN = 260;

/**
 * Aspect used when the caller doesn't supply the rendered box's own.
 *
 * Deliberately wide: the map is a band between the KPI row and the site
 * cards, and all three need to be on screen together. The artwork's own
 * aspect (~1.58) is far too tall for that — at a typical content width it
 * would render a map around a thousand pixels high.
 */
export const DEFAULT_ASPECT = 2.4;

/**
 * Frame width assumed before the box has been measured (and in jsdom, where
 * ResizeObserver does not exist).
 *
 * Only marker and label sizing reads it — those are specified in screen
 * pixels, which needs to know how many geometry units a pixel is worth. A
 * typical shell content column, so the fallback renders close to the real
 * thing rather than at some arbitrary scale.
 */
export const DEFAULT_FRAME_WIDTH_PX = 1200;

/** The artwork's full extent. */
const ART_WIDTH = GEOMETRY_BOUNDS.maxX - GEOMETRY_BOUNDS.minX;

/** Centre of the artwork, used when there is nothing to fit to. */
const ART_CENTRE_X = (GEOMETRY_BOUNDS.minX + GEOMETRY_BOUNDS.maxX) / 2;
const ART_CENTRE_Y = (GEOMETRY_BOUNDS.minY + GEOMETRY_BOUNDS.maxY) / 2;

/**
 * The whole artwork, framed at the default aspect.
 *
 * Used for marker scaling as a stable reference, and as the fallback view
 * when no site can be placed.
 */
export const FULL_VIEW: ViewBox = frame(
  ART_CENTRE_X,
  ART_CENTRE_Y,
  ART_WIDTH,
  ART_WIDTH / DEFAULT_ASPECT
);

/**
 * Build a viewBox centred on a point.
 *
 * The centre — not the box — is clamped to the artwork, so a wide, short
 * frame is allowed to extend past the top and bottom of the drawing. That
 * blank margin is just ocean and Canada; clamping the *box* inside the
 * artwork instead would slide a short frame down over the country and crop
 * the northern states out of a nationwide view.
 */
function frame(centreX: number, centreY: number, width: number, height: number): ViewBox {
  const cx = clamp(centreX, GEOMETRY_BOUNDS.minX, GEOMETRY_BOUNDS.maxX);
  const cy = clamp(centreY, GEOMETRY_BOUNDS.minY, GEOMETRY_BOUNDS.maxY);
  return { x: cx - width / 2, y: cy - height / 2, width, height };
}

/**
 * Compute the SVG viewBox that frames `points`.
 *
 * Handles, in order: no points (full map), a single point or a tight
 * cluster (MIN_SPAN floor), a wide spread (padded bounding box), and a
 * spread wider than the artwork (clamped to the artwork, never overflowing
 * into blank canvas).
 */
export function fitViewBox(
  points: readonly Point[],
  /** Aspect (w/h) of the box this will be rendered into. */
  aspect: number = DEFAULT_ASPECT
): ViewBox {
  const ratio = Number.isFinite(aspect) && aspect > 0 ? aspect : DEFAULT_ASPECT;
  if (points.length === 0) {
    return frame(ART_CENTRE_X, ART_CENTRE_Y, ART_WIDTH, ART_WIDTH / ratio);
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }

  const centreX = (minX + maxX) / 2;
  const centreY = (minY + maxY) / 2;

  let width = maxX - minX;
  let height = maxY - minY;
  if (width > 0 || height > 0) {
    if (width / height > ratio) height = width / ratio;
    else width = height * ratio;
  }

  width *= 1 + PADDING_RATIO * 2;
  height *= 1 + PADDING_RATIO * 2;

  if (width < MIN_SPAN) {
    width = MIN_SPAN;
    height = width / ratio;
  }

  return frame(centreX, centreY, width, height);
}

function clamp(value: number, lo: number, hi: number): number {
  // hi < lo when the frame is the full artwork; lo wins, which is correct.
  return Math.max(lo, Math.min(hi, value));
}

/** Serialise for the SVG `viewBox` attribute. */
export function viewBoxAttr(v: ViewBox): string {
  const r = (n: number) => Math.round(n * 100) / 100;
  return `${r(v.x)} ${r(v.y)} ${r(v.width)} ${r(v.height)}`;
}

/**
 * A short caption for where the fleet is, e.g. "Southeast US".
 *
 * The mock shows a hardcoded "Southeast US"; here it is derived from the
 * sites' own coordinates, so it stays true if the fleet moves. Coarse by
 * design — it labels the view, it is not a geographic claim about any
 * individual site.
 *
 * Derived from real coordinates, so this is not invented data; when sites
 * are spread too widely for one region to be meaningful it says so.
 */
export function regionLabel(coords: readonly { lat: number; lon: number }[]): string {
  if (coords.length === 0) return '';

  const latSpan = Math.max(...coords.map(c => c.lat)) - Math.min(...coords.map(c => c.lat));
  const lonSpan = Math.max(...coords.map(c => c.lon)) - Math.min(...coords.map(c => c.lon));
  if (latSpan > 12 || lonSpan > 20) return 'United States';

  const lat = coords.reduce((sum, c) => sum + c.lat, 0) / coords.length;
  const lon = coords.reduce((sum, c) => sum + c.lon, 0) / coords.length;

  if (lon <= -104) return 'Western US';
  if (lat >= 39.5) return lon > -82 ? 'Northeast US' : 'Midwest US';
  if (lon <= -94) return 'South Central US';
  return 'Southeast US';
}
